import React, { createContext, useContext, useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { RecruiterProfile } from "@/types";

interface RecruiterAuthContextType {
  session: Session | null;
  user: User | null;
  recruiterProfile: RecruiterProfile | null;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  isLoadingProfile: boolean;
}

const RecruiterAuthContext = createContext<RecruiterAuthContextType | undefined>(undefined);

export const RecruiterAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [recruiterProfile, setRecruiterProfile] = useState<RecruiterProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("🔵 Recruiter Auth state change:", event, session?.user?.id);
        console.log("🔵 User metadata:", session?.user?.user_metadata);
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Defer profile fetch with setTimeout to prevent deadlock
          setTimeout(() => {
            fetchRecruiterProfile(session.user);
          }, 0);
        } else {
          setRecruiterProfile(null);
          setIsLoadingProfile(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log("🔵 Initial recruiter session check:", session?.user?.id);
      console.log("🔵 User type from metadata:", session?.user?.user_metadata?.user_type);
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchRecruiterProfile(session.user);
      } else {
        setIsLoadingProfile(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchRecruiterProfile = async (currentUser?: any) => {
    const userToFetch = currentUser || user;
    if (!userToFetch) {
      console.log("🔵 No user found, skipping recruiter profile fetch");
      setIsLoadingProfile(false);
      return;
    }
    
    console.log("🔵 Fetching recruiter profile for user:", userToFetch.id);
    console.log("🔵 User type:", userToFetch.user_metadata?.user_type);
    setIsLoadingProfile(true);
    
    try {
      // Fetch profile data directly using maybeSingle to avoid errors when no profile exists
      const { data: profile, error } = await supabase
        .from("recruiter_profiles")
        .select("*")
        .eq("user_id", userToFetch.id)
        .maybeSingle();

      console.log("🔵 Recruiter profile fetch result:", { profile, error });

      if (error) {
        console.error("🔴 Error fetching recruiter profile:", error);
        setRecruiterProfile(null);
        setIsLoadingProfile(false);
        return;
      }

      if (profile) {
        console.log("✅ Successfully loaded recruiter profile:", profile);
        setRecruiterProfile(profile);
      } else {
        console.log("⚠️ No recruiter profile found for user:", userToFetch.id);
        console.log("⚠️ This user may be a job seeker, not a recruiter");
        setRecruiterProfile(null);
      }
    } catch (error) {
      console.error("🔴 Exception in fetchRecruiterProfile:", error);
      setRecruiterProfile(null);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const refreshProfile = async () => {
    await fetchRecruiterProfile();
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setRecruiterProfile(null);
  };

  return (
    <RecruiterAuthContext.Provider value={{
      session,
      user,
      recruiterProfile,
      signOut,
      refreshProfile,
      isLoadingProfile
    }}>
      {children}
    </RecruiterAuthContext.Provider>
  );
};

export const useRecruiterAuth = () => {
  const context = useContext(RecruiterAuthContext);
  if (context === undefined) {
    throw new Error("useRecruiterAuth must be used within a RecruiterAuthProvider");
  }
  return context;
};